import React from "react";
import { getImageProxyUrl } from './imageProxyUtil';
import { useSeriesGrouping } from '../hooks/useSeriesGrouping';
import { useAdaptiveTitle } from '../hooks/useAdaptiveTitle';
import { SeriesCard, SeasonHeader, EpisodeCard } from './SeriesCards';

// Componente separato per le card dei canali con titoli adattivi
function ChannelCard({ 
  item, 
  onSelect, 
  toggleFavorite, 
  isFavorite, 
  isGlobalMode, 
  globalSearchTerm 
}) {
  const { titleRef, titleClassName, containerClassName } = useAdaptiveTitle(item.name, 32);

  // Costruisci sempre la src tramite proxy, tranne se già passa dal proxy
  const logoSrc = getImageProxyUrl(item.tvgLogo);

  return (
    <div
      className="group relative backdrop-blur-sm border-2 rounded-xl transition-all duration-300 cursor-pointer overflow-hidden bg-gray-900/30 hover:bg-gray-800/50 border-gray-500/50 hover:border-blue-400/70 hover:shadow-2xl hover:shadow-blue-500/20 hover:scale-105"
      style={{ width: '170px', maxWidth: '170px', minWidth: '140px' }}
      onClick={() => onSelect(item)}
    >
      {/* Channel Image Container */}
      <div className="relative aspect-video overflow-hidden">
        {item.tvgLogo ? (
          <img 
            src={logoSrc} 
            alt="Logo" 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              if (e.target.src.includes('/proxy/image') && item.originalLogoUrl && !e.target.hasRetried) {
                e.target.hasRetried = true;
                const separator = e.target.src.includes('?') ? '&' : '?';
                e.target.src = e.target.src + separator + 'retry=' + Date.now();
              } else {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }
            }}
          />
        ) : null}
        
        {/* Fallback placeholder */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-4xl"
          style={{ display: item.tvgLogo ? 'none' : 'flex' }}
        >
          📺
        </div>
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Favorites Toggle */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(item);
          }}
          className="absolute top-2 left-2 p-1 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-4 h-4 ${isFavorite(item) ? 'text-red-500' : 'text-white'}`}
            fill={isFavorite(item) ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </button>

        {/* Group Badge */}
        {item.group && (
          <div className="absolute top-2 right-2">
            <span className="bg-black/50 backdrop-blur-sm text-white px-1.5 py-0.5 rounded-full" style={{ fontSize: '0.5rem' }}>
              {item.group}
            </span>
          </div>
        )}
      </div>
      
      {/* Channel Info con titolo adattivo */}
      <div className="p-3">
        <div className={containerClassName} style={{ maxHeight: '32px', minHeight: '32px' }}>
          <h3 
            ref={titleRef}
            className={`${titleClassName} font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors`}
          >
            {isGlobalMode && globalSearchTerm && item.name.toLowerCase().includes(globalSearchTerm.toLowerCase()) ? (
              item.name.split(new RegExp(`(${globalSearchTerm})`, 'gi')).map((part, i) => 
                part.toLowerCase() === globalSearchTerm.toLowerCase() ? (
                  <mark key={i} className="bg-yellow-400/30 text-yellow-200 px-0.5 rounded">
                    {part}
                  </mark>
                ) : part
              )
            ) : item.name}
          </h3>
        </div>
        
        {/* Nome file M3U sotto il titolo */}
        {item.playlistName && (
          <p className="text-gray-400 text-xs truncate mt-1">
            {item.playlistName}
          </p>
        )}
      </div>
    </div>
  );
}

function ChannelList({ 
  channels, 
  totalChannels = 0,
  search, 
  group, 
  onSelect, 
  dark = true, 
  isGlobalMode = false, 
  globalSearchTerm = '',
  enableSeriesGrouping = true,
  isLoading = false,
  loadingProgress = 0,
  loadingStats = null,
  toggleFavorite = () => {},
  isFavorite = () => false
}) {
  // Filtra i canali in base ai criteri
  const filtered = channels.filter((c) => {
    if (!c?.name) return false; // Skip se nome non valido
    const isPlaceholder = c.name.startsWith('----') || c.tvgName?.startsWith('----');
    if (isPlaceholder) return false;
    
    // In modalità globale, i canali sono già filtrati
    if (isGlobalMode) return true;
    
    const searchTerm = search?.toLowerCase() || '';
    const channelName = c.name?.toLowerCase() || '';
    const matchesSearch = channelName.includes(searchTerm);
    const matchesGroup = !group || group.trim() === "" || c.group === group;
    return matchesSearch && matchesGroup;
  });

  // Limita a 500 canali per performance (se non c'è ricerca o gruppo) - NON in modalità globale
  const displayChannels = (!isGlobalMode && !search && !group && filtered.length > 500) 
    ? filtered.slice(0, 500) 
    : filtered;

  // Usa il hook per il raggruppamento serie
  const {
    processedChannels,
    seriesStats,
    toggleSeriesExpansion,
    expandAllSeries,
    collapseAllSeries,
    isSeriesExpanded
  } = useSeriesGrouping(displayChannels, enableSeriesGrouping);

  return (
    <div className="w-full overflow-x-hidden">
      {/* Global Search Results Header */}
      {isGlobalMode && processedChannels.length > 0 && (
        <div className={`mb-4 backdrop-blur-sm rounded-lg p-4 border ${
          dark 
            ? 'bg-purple-500/10 border-purple-400/30 text-purple-200' 
            : 'bg-purple-100 border-purple-300 text-purple-800'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🌐</span>
            <span className="font-semibold">Risultati Ricerca Globale</span>
          </div>
          <div className="text-sm opacity-90">
            Trovati <strong>{displayChannels.length}</strong> canali per 
            "<strong>{globalSearchTerm}</strong>" in tutti i gruppi
          </div>
          <div className="text-xs mt-1 opacity-75">
            Gruppi coinvolti: {[...new Set(displayChannels.map(c => c.group))].join(', ')}
          </div>
          {enableSeriesGrouping && seriesStats.totalSeries > 0 && (
            <div className="text-xs mt-2 opacity-75">
              📺 Organizzati in <strong>{seriesStats.totalSeries}</strong> serie 
              ({seriesStats.totalEpisodes} episodi totali)
            </div>
          )}
        </div>
      )}

      {/* Series Grouping Controls - Solo se abilitato e ci sono serie */}
      {enableSeriesGrouping && seriesStats.totalSeries > 0 && (
        <div className={`mb-4 backdrop-blur-sm rounded-lg p-3 border ${
          dark 
            ? 'bg-indigo-500/10 border-indigo-400/30 text-indigo-200' 
            : 'bg-indigo-100 border-indigo-300 text-indigo-800'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm">📺</span>
              <span className="text-sm font-semibold">
                {seriesStats.totalSeries} Serie Trovate ({seriesStats.totalEpisodes} episodi)
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={expandAllSeries}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  dark 
                    ? 'bg-green-500/20 text-green-200 border border-green-400/30 hover:bg-green-500/30' 
                    : 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
                }`}
              >
                Espandi Tutte
              </button>
              <button
                onClick={collapseAllSeries}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  dark 
                    ? 'bg-red-500/20 text-red-200 border border-red-400/30 hover:bg-red-500/30' 
                    : 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200'
                }`}
              >
                Chiudi Tutte
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avviso limitazione canali - Solo in modalità locale */}
      {!isGlobalMode && !search && !group && filtered.length > 500 && (
        <div className={`mb-4 text-center backdrop-blur-sm rounded-lg p-3 border ${
          dark 
            ? 'text-amber-300 bg-amber-500/10 border-amber-400/20' 
            : 'text-amber-700 bg-amber-100/50 border-amber-300'
        }`}>
          <div className="text-sm">
            📋 Showing primi 500 di {filtered.length} canali. Usa la ricerca o seleziona un gruppo per vedere di più.
          </div>
        </div>
      )}
      
      {/* Griglia Canali/Serie Dinamica */}
      <div className="channel-grid w-full max-w-full">
        {processedChannels.map((item, index) => {
          const key = `${item.type}-${item.name || item.seriesKey}-${item.url || index}-${index}`;
          
          if (item.type === 'series') {
            // Card Serie
            return (
              <SeriesCard
                key={key}
                series={item}
                dark={dark}
                isExpanded={isSeriesExpanded(item.seriesKey)}
                onToggleExpansion={toggleSeriesExpansion}
                onSelectEpisode={onSelect}
                globalSearchTerm={globalSearchTerm}
                isGlobalMode={isGlobalMode}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
              />
            );
          } else if (item.type === 'season-header') {
            // Header Stagione
            return (
              <SeasonHeader
                key={key}
                season={item}
                dark={dark}
                isGlobalMode={isGlobalMode}
              />
            );
          } else if (item.type === 'episode') {
            // Card Episodio
            return (
              <EpisodeCard
                key={key}
                episode={item}
                dark={dark}
                onSelect={onSelect}
                globalSearchTerm={globalSearchTerm}
                isGlobalMode={isGlobalMode}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
              />
            );
          } else {
            // Card Canale Normale con titolo adattivo
            return (
              <ChannelCard 
                key={key}
                item={item}
                onSelect={onSelect}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                isGlobalMode={isGlobalMode}
                globalSearchTerm={globalSearchTerm}
              />
            );
          }
        })}
      </div>

      {/* Barra di caricamento durante il parsing M3U */}
      {isLoading && (
        <div className={`text-center mt-8 backdrop-blur-sm rounded-xl p-6 border ${
          dark 
            ? 'text-white/60 bg-white/5 border-white/10' 
            : 'text-gray-600 bg-black/5 border-black/10'
        }`}>
          <div className="space-y-4">
            <div className="text-3xl mb-3">⏳</div>
            <div className={`text-lg font-bold mb-3 ${dark ? 'text-white/80' : 'text-gray-800'}`}>
              Caricamento playlist M3U in corso...
            </div>
            
            {/* Barra di progresso */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-3 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  dark 
                    ? 'bg-gradient-to-r from-purple-400 to-pink-400' 
                    : 'bg-gradient-to-r from-purple-600 to-pink-600'
                }`}
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
            
            {/* Percentuale */}
            <div className={`text-sm font-medium ${dark ? 'text-white/70' : 'text-black/70'}`}>
              {loadingProgress}% completato
            </div>
          </div>
        </div>
      )}

      {/* Messaggio se non ci sono canali dopo il caricamento */}
      {!isLoading && channels.length === 0 && (
        <div className={`text-center mt-8 backdrop-blur-sm rounded-xl p-6 border ${
          dark 
            ? 'text-white/60 bg-white/5 border-white/10' 
            : 'text-gray-600 bg-black/5 border-black/10'
        }`}>
          {totalChannels > 0 && loadingStats ? (
            // Statistiche quando il caricamento è completato ma il filtro corrente non mostra risultati
            <div className="space-y-4">
              <div className="text-3xl mb-3">🔍</div>
              <div className={`text-lg font-bold mb-3 ${dark ? 'text-white/80' : 'text-gray-800'}`}>
                Nessun risultato per il filtro corrente
              </div>
              
              {/* Statistiche compatte */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className={`p-3 rounded-lg backdrop-blur-sm ${
                  dark ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
                }`}>
                  <div className="text-red-400 font-bold text-lg">📺 {loadingStats.live}</div>
                  <div className="text-xs opacity-70">Canali LIVE TV</div>
                </div>
                <div className={`p-3 rounded-lg backdrop-blur-sm ${
                  dark ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
                }`}>
                  <div className="text-blue-400 font-bold text-lg">🎬 {loadingStats.films}</div>
                  <div className="text-xs opacity-70">Film/Video</div>
                </div>
                <div className={`p-3 rounded-lg backdrop-blur-sm ${
                  dark ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
                }`}>
                  <div className="text-green-400 font-bold text-lg">📁 {loadingStats.groups}</div>
                  <div className="text-xs opacity-70">Gruppi</div>
                </div>
                <div className={`p-3 rounded-lg backdrop-blur-sm ${
                  dark ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/10'
                }`}>
                  <div className="text-purple-400 font-bold text-lg">🎯 {loadingStats.total}</div>
                  <div className="text-xs opacity-70">Totali</div>
                </div>
              </div>
              
              <div className="text-xs opacity-60 mt-3">
                Cambia il filtro LIVE TV/FILM o seleziona un altro gruppo per vedere i canali
              </div>
            </div>
          ) : (
            // Messaggio default quando non c'è playlist caricata
            <div>
              <div className="text-4xl mb-3">📺</div>
              <div className={`text-lg font-bold mb-2 ${dark ? 'text-white/80' : 'text-gray-800'}`}>Nessuna playlist caricata</div>
              <div className="text-xs opacity-80">Carica un file M3U o usa il pulsante "KAINO M3U" nell'header per iniziare</div>
            </div>
          )}
        </div>
      )}
      
      {/* Messaggio se ci sono canali ma nessuno filtrato */}
      {channels.length > 0 && processedChannels.length === 0 && (
        <div className={`text-center mt-8 backdrop-blur-sm rounded-xl p-6 border ${
          dark 
            ? 'text-white/60 bg-white/5 border-white/10' 
            : 'text-gray-600 bg-black/5 border-black/10'
        }`}>
          <div className="text-4xl mb-3">{isGlobalMode ? '🌐' : '🔍'}</div>
          <div className={`text-lg font-bold mb-2 ${dark ? 'text-white/80' : 'text-gray-800'}`}>
            {isGlobalMode ? 'Nessun risultato nella ricerca globale' : 'Nessun canale trovato'}
          </div>
          <div className="text-xs opacity-80">
            {isGlobalMode && globalSearchTerm && `Nessun canale corrisponde alla ricerca globale "${globalSearchTerm}"`}
            {!isGlobalMode && search && `Nessun canale corrisponde alla ricerca "${search}"`}
            {!isGlobalMode && !search && group && `Nessun canale nel gruppo "${group}"`}
            {!isGlobalMode && !search && !group && "Prova a cambiare i filtri di ricerca"}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChannelList;
